import {
  Component,
  OnInit,
  PLATFORM_ID,
  OnDestroy,
  inject,
  computed,
  signal,
  effect,
  linkedSignal,
  ChangeDetectionStrategy,
  DestroyRef,
} from '@angular/core';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { CartItem } from '@pedix-workspace/utils';

import { ToastrService } from 'ngx-toastr';
import { cloneDeep } from 'lodash-es';

import { isPlatformServer, NgClass, AsyncPipe, isPlatformBrowser } from '@angular/common';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { nanoid } from 'nanoid';
import {
  EstablishmentUrlPipe,
  IsCategoryAvailablePipe,
} from '@pedix-workspace/pedixapp-shared-pipes';
import { AnalyticsService } from '@pedix-workspace/pedixapp-core-services';
import { StockService } from '@pedix-workspace/pedixapp-stock';
import { SeoService } from '../../main/seo.service';
import { EstablishmentWorkingHoursService } from '../../main/establishment-working-hours.service';
import { ApiV1_Category, ApiV1_Product } from '@pedix-workspace/api-v1';
import { PropInSelectedLanguagePipe } from '@pedix-workspace/pedixapp-shared-pipes';
import {
  ProductTagComponent,
  ProductDetailsComponent,
  ProductQuantitySelectorComponent,
  CategoryAvailabilityDialogComponent,
  getProductDetailsType,
} from '@pedix-workspace/pedixapp-shared-catalog';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import {
  IconBackComponent,
  IconClockComponent,
  IconLoadingComponent,
} from '@pedix-workspace/angular-ui-icons';
import { AppRequestContextService } from '../../../app-request-context.service';
import { AppCartItemsService } from '../../../app-cart-items.service';
import { FormsModule, NgForm } from '@angular/forms';
import { ProductTagsWithQuantitiesPipe } from '@pedix-workspace/pedixapp-shared-pipes';
import { AlertComponent } from '@pedix-workspace/angular-ui-alert';
import { createOrUpdateCartItem } from '@pedix-workspace/orders';
import { ShareButtonComponent } from '../../main/share-button/share-button.component';
import { CartPromotionsService } from '../../../cart-promotions.service';
import { WorkingHoursMessageDialogComponent } from '@pedix-workspace/pedixapp-shared-catalog';
import { EstablishmentsCheckoutService } from '../../order-checkout/establishments-checkout.service';
import { ModalDialogService } from '@pedix-workspace/angular-ui-modal';

@Component({
  selector: 'app-product',
  templateUrl: './product.component.html',
  styleUrls: ['./product.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    NgClass,
    IconBackComponent,
    IconLoadingComponent,
    ButtonComponent,
    ProductQuantitySelectorComponent,
    AsyncPipe,
    PropInSelectedLanguagePipe,
    IsCategoryAvailablePipe,
    FormsModule,
    RouterLink,
    ProductTagsWithQuantitiesPipe,
    ProductTagComponent,
    ProductDetailsComponent,
    AlertComponent,
    IconClockComponent,
    CategoryAvailabilityDialogComponent,
    ShareButtonComponent,
  ],
  // ProductComponent uses its own version of StockService (check providers[]); this is on purpose, leave it this way
  providers: [StockService],
})
export class ProductComponent implements OnInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);

  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private toastrService = inject(ToastrService);
  private analytics = inject(AnalyticsService);
  private seoService = inject(SeoService);
  private t = inject(TranslocoService);
  private establishmentUrlPipe = inject(EstablishmentUrlPipe);
  private appRequestContext = inject(AppRequestContextService);
  private stockService = inject(StockService);
  private establishmentWorkingHours = inject(EstablishmentWorkingHoursService);
  private appCartItemsService = inject(AppCartItemsService);
  private destroyRef = inject(DestroyRef);
  private cartPromotionsService = inject(CartPromotionsService);
  private modalDialogService = inject(ModalDialogService);
  private establishmentsCheckoutService = inject(EstablishmentsCheckoutService);

  readonly product = signal<ApiV1_Product | null>(null);
  readonly category = signal<ApiV1_Category | null>(null);
  readonly cartItem = signal<CartItem | null>(null);
  readonly cartItemSource = signal<CartItem | null>(null);
  readonly isFormSubmitted = signal<boolean>(false);

  readonly openedStatus = toSignal(this.establishmentWorkingHours.openedStatus$);

  readonly cartItemId = linkedSignal(() => {
    return this.cartItemSource()?.id || nanoid();
  });
  readonly productQuantity = linkedSignal(() => {
    return this.cartItemSource()?.qty || 1;
  });
  readonly productObservations = linkedSignal(() => {
    return this.cartItemSource()?.observations || '';
  });
  readonly selectedPresentationOption = linkedSignal(() => {
    return this.cartItemSource()?.selectedPresentation || null;
  });
  readonly selectedOptions = linkedSignal(() => {
    const cartItem = this.cartItemSource();
    const product = this.product();

    if (cartItem?.selectedOptions && product?.options) {
      return product.options.map(embeededOption =>
        cartItem.selectedOptions.find(
          cartItemOption => cartItemOption.option.id === embeededOption.id,
        ),
      );
    }
    return [];
  });

  readonly establishment = computed(() => this.appRequestContext.establishment());
  readonly activeProducts = computed(() => this.appRequestContext.activeProducts());
  readonly selectedLanguage = computed(() => this.appRequestContext.selectedLanguage());

  readonly isCartDisabled = computed(() => this.openedStatus()?.isCartDisabled ?? false);
  readonly editMode = computed(() => this.cartItemSource() !== null);
  readonly maxQuantity = computed(() => {
    const product = this.product();
    const selectedPresentationOption = this.selectedPresentationOption();
    const stock = this.stockService.stock();

    if (product.presentations) {
      if (!product.presentations.displayItemQuantities && selectedPresentationOption) {
        const stockInfo = stock.getStockInfoByItem(selectedPresentationOption.items[0].id);
        return stockInfo.hasUnlimitedStock ? undefined : stockInfo.availableStock;
      }
    } else {
      const stockInfo = stock.getStockInfoByItem(product.id);
      return stockInfo.hasUnlimitedStock ? undefined : stockInfo.availableStock;
    }
    return undefined;
  });
  readonly displayShareButton = computed(() => {
    return this.establishment().seoSocialMedia
      ? this.establishment().seoSocialMedia.displayShareButton
      : true;
  });
  readonly displaySku = computed(() => {
    return this.appRequestContext.shouldDisplaySku();
  });
  readonly displayProductPriceWithoutTaxes = computed(() => {
    return this.establishment().catalogConfiguration.displayProductPriceWithoutTaxes;
  });
  readonly purchaseOptions = this.establishmentsCheckoutService.purchaseOptions;
  readonly isWithoutStock = computed(() => {
    return this.stockService.isProductWithoutStock({
      id: this.product().id,
      presentationItems: this.product().presentations?.items,
    });
  });
  readonly productShareUrl = computed(() => {
    const subsidiarySelector = this.appRequestContext.subsidiarySelector();
    const establishment = subsidiarySelector
      ? this.appRequestContext.subsidiaryParentCatalog()?.establishment
      : this.establishment();

    return this.establishmentUrlPipe.transform(establishment, [
      'categoria',
      this.category()?.slug,
      'producto',
      this.product()?.slug,
    ]);
  });
  readonly establishmentCategory = computed(() => {
    return this.establishment().categories?.[0] || null;
  });
  readonly withPresentations = computed(() => {
    return !!this.product().presentations;
  });
  readonly productType = computed(() => getProductDetailsType(this.product()));
  readonly hasStockIssues = computed(() => {
    const cartItem = this.cartItem();

    if (isPlatformServer(this.platformId)) {
      return false;
    }

    return this.stockService
      .stock()
      .getStockInfoArrayByCartItem(cartItem.id)
      .some(stockInfo => stockInfo.getMissingStockByCartItem(cartItem.id) > 0);
  });

  readonly productPromotions = computed(() => {
    const product = this.product();
    if (!product) return [];
    return this.cartPromotionsService.availablePromotionsByProductId().get(product.id) ?? [];
  });

  constructor() {
    effect(() => {
      const product = this.product();
      const establishment = this.establishment();
      const category = this.category();

      if (product && establishment) {
        this.seoService.setProductMetadata(product, establishment, category?.id);
      }
    });
    if (isPlatformBrowser(this.platformId)) {
      effect(() => {
        const product = this.product();
        const category = this.category();
        const establishmentCategory = this.establishmentCategory();

        if (product && category && establishmentCategory) {
          this.analytics.viewProduct(product, category, establishmentCategory);
        }
      });
      effect(() => {
        const cartItem = this.cartItem();
        const cartItemsCopy = this.appCartItemsService.cartItems().slice(0);

        const index = cartItemsCopy.findIndex(current => current.id === cartItem.id);

        if (index === -1) {
          cartItemsCopy.push(cartItem);
        } else {
          cartItemsCopy.splice(index, 1, cartItem);
        }

        this.stockService.cartItems.set(cloneDeep(cartItemsCopy));
      });
    }
  }

  ngOnInit() {
    this.activatedRoute.data
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ category, product, cartItem }) => {
        if (cartItem) {
          const category = this.appRequestContext
            .allCategories()
            .find(category => category.id === cartItem.category.id);

          const product = category?.products.find(
            categoryProduct => categoryProduct.id === cartItem.product.id,
          );

          this.category.set(cloneDeep(category));
          this.product.set(cloneDeep(product));

          this.cartItemSource.set(cloneDeep(cartItem));
          this.cartItem.set(this.createOrUpdateCartItem());
        } else if (category && product) {
          this.category.set(cloneDeep(category));
          this.product.set(cloneDeep(product));

          this.cartItemSource.set(null);
          this.cartItem.set(this.createOrUpdateCartItem());
        }
      });
  }

  onStockSelectionChanged() {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    const cartItem = this.createOrUpdateCartItem();

    this.cartItem.set(cartItem);
  }

  ngOnDestroy() {
    this.seoService.setDefaultMetadata();
  }

  back() {
    if (this.editMode()) {
      this.router.navigate([`../../`], { relativeTo: this.activatedRoute });
    } else {
      if (
        this.activatedRoute.snapshot.queryParams['q'] ||
        this.activatedRoute.snapshot.queryParams['qt']
      ) {
        this.router.navigate([`/busqueda`], {
          queryParams: {
            q: this.activatedRoute.snapshot.queryParams['q'],
            qt: this.activatedRoute.snapshot.queryParams['qt'],
          },
        });
      } else if (this.activatedRoute.snapshot.queryParams['t']) {
        this.router.navigate([`/productos-relacionados`], {
          queryParams: { t: this.activatedRoute.snapshot.queryParams['t'] },
        });
      } else {
        this.router.navigate(['../..'], {
          relativeTo: this.activatedRoute,
        });
      }
    }
  }

  addToCart($event: FormDataEvent, productForm: NgForm, product: ApiV1_Product) {
    this.isFormSubmitted.set(true);

    if (!productForm.valid) {
      this.toastrService.error(this.t.translate('validation.fixErrors'));

      return;
    }
    const cartItem = this.createOrUpdateCartItem();

    if (this.editMode()) {
      this.appCartItemsService.updateCartItem(cartItem);
    } else {
      this.appCartItemsService.addCartItem(cartItem);
    }
    this.back();
  }

  displayWorkingHours(event: Event) {
    event.stopPropagation();

    this.modalDialogService
      .attachDialog(WorkingHoursMessageDialogComponent)
      .open(this.establishment().workingDays);
  }

  private createOrUpdateCartItem(): CartItem {
    return createOrUpdateCartItem({
      category: this.category(),
      product: this.product(),
      establishmentId: this.establishment().id,
      cartItemId: this.cartItemId(),
      selectedPresentationOption: this.selectedPresentationOption(),
      productQuantity: this.productQuantity(),
      productObservations: this.productObservations(),
      selectedOptions: this.selectedOptions(),
    });
  }
}
